/**
 * Management fee settlement — pay approved internal PM fee bills from operating cash (API + web).
 * Shapes stay aligned with `settleManagementFeeBills` in apps/api.
 */

export const MANAGEMENT_FEE_SETTLEMENT_BILL_STATUSES = [
  'success_paid',
  'skipped_insufficient_cash',
  'skipped_reserve_violation',
  'skipped_already_paid',
  'error',
] as const;

export type ManagementFeeSettlementBillStatus = (typeof MANAGEMENT_FEE_SETTLEMENT_BILL_STATUSES)[number];

export type ManagementFeeSettlementBillRow = {
  billId: string;
  propertyId: string;
  propertyName: string;
  invoiceNumber?: string;
  dueDate: string;
  amountCents: number;
  status: ManagementFeeSettlementBillStatus;
  /** Pay failure message or operator context. */
  detail?: string;
};

export type ManagementFeeSettlementReport = {
  operatingOrganizationId: string;
  /** When set, only bills whose `invoiceNumber` ends with `_${yyyyMm}` were eligible. */
  filterYyyyMm: string | null;
  /** UTC calendar date used for cash payment (`paidDate` / payment row date), not accrual period. */
  settlementDate: string;
  billCount: number;
  rows: ManagementFeeSettlementBillRow[];
  summary: {
    successPaid: number;
    skippedInsufficientCash: number;
    skippedReserveViolation: number;
    skippedAlreadyPaid: number;
    errors: number;
  };
};

/** POST /accounting/management-fee-settlements request body (aligned with API Zod schema). */
export type ManagementFeeSettlementPostBody = {
  operatingOrganizationId: string;
  /** Optional `YYYY-MM` — limits to accrual invoices for that month (`mgt_fee_*_${yyyyMm}`). */
  yyyyMm?: string;
};
